// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where the DMD goes on the backglass (M3-17).
//
// The backglass art paints a window for the display — NEON NOVA's `dmdWindow` op, at
// normalised centre (0.5, 0.82) and size (0.62, 0.2) — and the panel has to land exactly on
// it. A few millimetres out and the glass shows a black rim on one side and a clipped dot row
// on the other, which reads as a rendering bug in the panel rather than as a placement error.
//
// Two conversions, and the second is the one that bites:
//
//   * Normalised to metres, against the glass mesh's own size rather than the backbox's,
//     because the glass is inset and the art is painted on the glass.
//   * **Canvas `y` down to mesh `y` up.** A backglass art program is an ordinary top-down
//     picture — unlike a playfield's, which is authored y-up in PH-001's frame (see
//     `artTexture.ts`) — so a window painted at 0.82 is near the *bottom*, and a mesh placed
//     at +0.32 of the height is near the top. That mistake puts the DMD where the wordmark is
//     and looks, from the front, like the artist put it there.
//
// No Three.js here. The arithmetic is the part that can be wrong, so the arithmetic is the
// part that is tested.

/** A normalised art-program window: centre and size in 0…1 of the surface, `y` down. */
export interface ArtWindow {
  readonly centre: readonly [number, number]
  readonly size: readonly [number, number]
}

/** A panel in the glass mesh's own frame: metres, origin at the glass centre, `y` up. */
export interface PanelPlacement {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

/**
 * Find the display window an art program paints, if it paints one.
 *
 * Reads the machine's own art rather than taking a position from somewhere else, so a machine
 * that moves its DMD moves the panel with it and cannot get the two out of step. A machine
 * with a display and no window is not an error — a segment display or an LCD bezel is drawn
 * some other way — so this answers `undefined` and lets the caller decide.
 */
export function findDmdWindow(program: unknown): ArtWindow | undefined {
  if (!Array.isArray(program)) return undefined
  for (const raw of program) {
    if (typeof raw !== 'object' || raw === null) continue
    const op = raw as Record<string, unknown>
    if (op['op'] !== 'dmdWindow') continue
    const centre = op['centre']
    const size = op['size']
    if (!isPair(centre) || !isPair(size)) continue
    return { centre, size }
  }
  return undefined
}

/**
 * Place a normalised art window on a glass of a given size, in mesh coordinates.
 *
 * `inset` shrinks the panel inside the painted window so the art's own frame stays visible
 * around it. A DMD sitting flush to the edge of its aperture looks like a screenshot pasted
 * onto the glass; a couple of millimetres of painted bezel is what makes it look fitted.
 */
export function placePanel(
  window: ArtWindow,
  glassWidth: number,
  glassHeight: number,
  inset = 0.004,
): PanelPlacement {
  const width = Math.max(0.001, window.size[0] * glassWidth - inset * 2)
  const height = Math.max(0.001, window.size[1] * glassHeight - inset * 2)
  return {
    x: (window.centre[0] - 0.5) * glassWidth,
    // Flipped: the art's `y` runs down the canvas, the mesh's runs up.
    y: (0.5 - window.centre[1]) * glassHeight,
    width,
    height,
  }
}

function isPair(v: unknown): v is readonly [number, number] {
  return Array.isArray(v) && v.length === 2 && typeof v[0] === 'number' && typeof v[1] === 'number'
}
